import { useQuery } from "@tanstack/react-query";
import { Checkbox, DatePicker, Input, InputNumber, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { api } from "../api/client";
import type { Page } from "../api/client";

interface Definition {
  id: string;
  code: string;
  name: string;
  type: "string" | "number" | "boolean" | "date" | "enum";
  options: string[];
  required: boolean;
  sort_order: number;
}

interface Props {
  entity: string;
  value?: Record<string, unknown> | null;
  onChange?: (value: Record<string, unknown>) => void;
  disabled?: boolean;
}

/** Extra attributes (ФТ-1.9): rendered from attribute_definitions, stored in the record's JSON `attributes`. */
export function AttributesField({ entity, value, onChange, disabled }: Props) {
  const defs = useQuery({
    queryKey: ["attribute_definitions", entity],
    queryFn: () => api.get<Page<Definition>>("/attribute_definitions", { size: 200, filter: { field: "entity", op: "eq", value: entity } }),
  });
  const current = value ?? {};
  const set = (code: string, v: unknown) => onChange?.({ ...current, [code]: v === "" ? null : v });
  const rows = defs.data?.items ?? [];
  if (!rows.length) {
    return <Typography.Text type="secondary">Дополнительные реквизиты не заданы (справочник «Дополнительные реквизиты»)</Typography.Text>;
  }
  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      {rows.map((d) => {
        const v = current[d.code];
        let control;
        if (d.type === "boolean") control = <Checkbox checked={!!v} disabled={disabled} onChange={(e) => set(d.code, e.target.checked)} />;
        else if (d.type === "number") control = <InputNumber value={typeof v === "number" ? v : v ? Number(v) : undefined} disabled={disabled} onChange={(n) => set(d.code, n)} style={{ width: 200 }} />;
        else if (d.type === "date") control = <DatePicker value={v ? dayjs(String(v)) : undefined} format="DD.MM.YYYY" disabled={disabled} onChange={(day) => set(d.code, day ? day.format("YYYY-MM-DD") : null)} />;
        else if (d.type === "enum") control = <Select value={(v as string) ?? undefined} allowClear disabled={disabled} options={(d.options ?? []).map((o) => ({ value: o, label: o }))} onChange={(s) => set(d.code, s ?? null)} style={{ width: 240 }} />;
        else control = <Input value={(v as string) ?? ""} disabled={disabled} onChange={(e) => set(d.code, e.target.value)} style={{ width: 320 }} />;
        return (
          <div key={d.id} style={{ display: "grid", gridTemplateColumns: "220px 1fr", alignItems: "center", gap: 8 }}>
            <Typography.Text>
              {d.name}
              {d.required ? " *" : ""}
            </Typography.Text>
            {control}
          </div>
        );
      })}
    </Space>
  );
}
